/**
 * "TRF groups" — the WhatsApp groups TRF itself runs (w1023c L3, Victor
 * 2026-10-23: "some WhatsApp groups are TRF's: TRF holds admin power").
 *
 * A TRF group is a staff Resources entry in the Community category with
 * `run_by_trf: true` (`(?) trf-group-flag`), matched to the family exactly as
 * `/circle` matches every other resource (country, treatment phase, state).
 * Outside groups — Community entries TRF does not run — no longer show here;
 * they live on the family's Resources page with the "not run by TRF" line.
 * Staff add, flag or retire a group on /trfer/resources; nothing is
 * hard-coded here.
 *
 * Until 2026-10-23 this section listed the outside groups under "Where
 * families already talk" (the 2026-09-10 rebuild's research: peer
 * organisations point families to groups run by trusted organisations).
 */
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { ExternalLink as ExternalLinkIcon, MessageCircle } from 'lucide-react';
import { ExternalLink } from '@/components/ui/ExternalLink';
import { MonoLabel, SectionTitle } from '@/components/editorial';
import { api } from '@/services/api';
import { isTrfGroup, isWhatsAppLink } from '@/lib/resource-display';
import type { Resource } from '@/types/api';

export function CommunityGroupsSection() {
  const { t } = useTranslation(['community', 'common']);
  const { data, isLoading, isError } = useQuery({
    queryKey: ['circle', 'resources'],
    queryFn: () => api.circle.getResources(),
    staleTime: 5 * 60_000,
  });
  const groups: Resource[] = (data?.data ?? []).filter(isTrfGroup);

  return (
    <section aria-labelledby="community-groups-title" className="space-y-3">
      <SectionTitle id="community-groups-title">{t('groups.trfTitle')}</SectionTitle>
      <p className="text-body text-stevens-ink-3">{t('groups.trfIntro')}</p>

      {isLoading && <p className="text-body text-stevens-ink-3">{t('common:states.loading')}</p>}
      {isError && <p className="text-body text-stevens-ink-3">{t('groups.loadError')}</p>}
      {!isLoading && !isError && groups.length === 0 && (
        <p className="text-body text-stevens-ink-3">{t('groups.empty')}</p>
      )}

      {groups.length > 0 && (
        <ul className="divide-y divide-border/60 rounded-lg border border-border bg-card">
          {groups.map((g) => {
            const whatsApp = isWhatsAppLink(g.url);
            return (
              <li key={g._id} className="flex flex-wrap items-center gap-x-4 gap-y-2 px-5 py-3">
                <div className="flex-1 min-w-[12rem]" translate="no">
                  <p className="text-body font-medium text-foreground">{g.title}</p>
                  {g.description && <p className="text-body text-stevens-ink-3">{g.description}</p>}
                  {g.eligibility_note && <MonoLabel className="block mt-0.5 font-normal">{g.eligibility_note}</MonoLabel>}
                </div>
                {g.url && (
                  <ExternalLink
                    href={g.url}
                    data-testid="community-group-join"
                    className="inline-flex min-h-11 items-center gap-1.5 rounded-md border border-border px-3 text-body font-medium text-stevens-indigo hover:bg-muted"
                  >
                    {whatsApp
                      ? <MessageCircle className="h-4 w-4" aria-hidden />
                      : <ExternalLinkIcon className="h-4 w-4" aria-hidden />}
                    {whatsApp ? t('groups.joinWhatsApp') : t('groups.join')}
                  </ExternalLink>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );
}
